'use client';

import Image from 'next/image';
import Script from 'next/script';
import React from 'react';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';

import { useLanguage } from '@/contexts/LanguageContext';

export default function OpDrBarisCinPage() {
  const { t } = useLanguage();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name: 'Op. Dr. Barış Çin',
    image: '/images/doktor.webp',
    description: t.doctorInfo.biographyText1,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kadıköy',
      addressRegion: 'Istanbul',
      addressCountry: 'TR'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '40.9923',
      longitude: '29.0277'
    },
    url: 'https://www.drbariscin.com',
    telephone: '+905551234567',
    priceRange: '$$',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      opens: '09:00',
      closes: '18:00'
    }
  };

  return (
    <>
      <Script
        id="op-dr-baris-cin-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className='min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-16 px-4'>
        <div className='max-w-5xl mx-auto'>
          {/* SEO H1 - Gizli ama arama motorları için mevcut */}
          <h1 className="sr-only">Op. Dr. Barış Çın - Estetik Cerrahi Uzmanı</h1>
          {/* Breadcrumb */}
          <div className='mt-12 mb-8'>
            <Breadcrumb
              items={[
                { label: t.header.home, href: '/' },
                { label: t.header.about, href: '#hakkimizda' },
                { label: 'Op. Dr. Barış Çin', href: '/tr/op-dr-baris-cin' },
              ]}
            />
          </div>
          {/* Doctor Info */}
          <div className='bg-white rounded-3xl shadow-xl flex flex-col md:flex-row items-center md:items-stretch overflow-hidden mb-8'>
            {/* Left: Doctor Photo */}
            <div className='md:w-1/2 w-full flex items-center justify-center bg-gray-100 p-8'>
              <Image
                src='/images/doktor.webp'
                alt='Op. Dr. Barış Çin'
                width={350}
                height={420}
                className='rounded-2xl object-cover shadow-lg'
              />
            </div>
            {/* Right: Info */}
            <div className='md:w-1/2 w-full flex flex-col justify-center p-8'>
              <div className='mb-8'>
                <h2 className='text-3xl font-bold text-[#181C32] mb-6'>
                  {t.doctorInfo.title}
                </h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-gray-700 text-base'>
                  <div>{t.doctorInfo.name}:</div>
                  <div className='font-semibold'>Op. Dr. Barış Çin</div>
                  <div>{t.doctorInfo.specialty}:</div>
                  <div className='font-semibold'>{t.doctorInfo.specialtyValue}</div>
                  <div>{t.doctorInfo.email}:</div>
                  <div className='font-semibold'>drbariscin86@gmail.com</div>
                  <div>{t.doctorInfo.phone}:</div>
                  <div className='font-semibold'>(+90) 543-899 95 86</div>
                  <div>{t.doctorInfo.socialMedia}:</div>
                  <div className='flex gap-3 mt-1'>
                    <a href='https://www.facebook.com/drbariscin86/?locale=tr_TR' target="_blank" rel="noopener noreferrer" className='text-blue-600 hover:text-blue-800'>
                      <FaFacebookF size={20} />
                    </a>
                    <a href='https://x.com/drbariscin' target="_blank" rel="noopener noreferrer" className='text-blue-400 hover:text-blue-600'>
                      <FaTwitter size={20} />
                    </a>
                    <a href='https://www.linkedin.com/in/op-dr-baris-cin-9b918aa0/?originalSubdomain=tr' target="_blank" rel="noopener noreferrer" className='text-blue-700 hover:text-blue-900'>
                      <FaLinkedinIn size={20} />
                    </a>
                    <a href='https://www.instagram.com/drbariscin/?hl=tr' target="_blank" rel="noopener noreferrer" className='text-pink-600 hover:text-pink-800'>
                      <FaInstagram size={20} />
                    </a>
                  </div>
                </div>
              </div>
              <div>
                <h2 className='text-2xl font-bold text-[#181C32] mb-6'>
                  {t.doctorInfo.educationExperience}
                </h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-gray-700 text-base'>
                  <div>{t.doctorInfo.education}:</div>
                  <div className='font-semibold'>{t.doctorInfo.educationValue}</div>
                  <div>{t.doctorInfo.experience}:</div>
                  <div className='font-semibold'>{t.doctorInfo.experienceValue}</div>
                  <div>{t.doctorInfo.expertise}:</div>
                  <div className='font-semibold'>{t.doctorInfo.expertiseValue}</div>
                </div>
              </div>
            </div>
          </div>
          {/* Biography & Skills Section */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch'>
            {/* Biography */}
            <div className='bg-white/80 rounded-2xl p-8 flex flex-col justify-center shadow-lg'>
              <h2 className='text-2xl font-bold text-[#181C32] mb-4'>
                {t.doctorInfo.biography}
              </h2>
              <p className='text-gray-700 mb-4'>
                {t.doctorInfo.biographyText1}
              </p>
              <p className='text-gray-700 mb-4'>
                {t.doctorInfo.biographyText2}
              </p>
              <div className='text-gray-700'>
                <p>{t.doctorInfo.europeanBoard}</p>
                <p>{t.doctorInfo.turkishBoard}</p>
              </div>
            </div>
            {/* Professional Skills */}
            <div className='relative rounded-2xl overflow-hidden flex flex-col justify-center shadow-lg min-h-[340px]'>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-blue-800" />
              <div className='relative z-10 p-8 text-white'>
                <h2 className='text-2xl font-bold mb-4'>
                  {t.doctorInfo.professionalSkills}
                </h2>
                <p className='mb-6 text-white/80'>
                  {t.doctorInfo.skillsDescription}
                </p>
                <div className='space-y-4'>
                  <div>
                    <div className='flex justify-between mb-1'>
                      <span>{t.doctorInfo.facialAesthetics}</span>
                      <span>95%</span>
                    </div>
                    <div className='w-full h-2 bg-white/30 rounded-full'>
                      <div className='h-2 bg-blue-400 rounded-full' style={{ width: '95%' }} />
                    </div>
                  </div>
                  <div>
                    <div className='flex justify-between mb-1'>
                      <span>{t.doctorInfo.bodyAesthetics}</span>
                      <span>90%</span>
                    </div>
                    <div className='w-full h-2 bg-white/30 rounded-full'>
                      <div className='h-2 bg-blue-400 rounded-full' style={{ width: '90%' }} />
                    </div>
                  </div>
                  <div>
                    <div className='flex justify-between mb-1'>
                      <span>{t.doctorInfo.rhinoplasty}</span>
                      <span>95%</span>
                    </div>
                    <div className='w-full h-2 bg-white/30 rounded-full'>
                      <div className='h-2 bg-blue-400 rounded-full' style={{ width: '95%' }} />
                    </div>
                  </div>
                  <div>
                    <div className='flex justify-between mb-1'>
                      <span>{t.doctorInfo.breastAesthetics}</span>
                      <span>90%</span>
                    </div>
                    <div className='w-full h-2 bg-white/30 rounded-full'>
                      <div className='h-2 bg-blue-400 rounded-full' style={{ width: '90%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Eğitim ve Akademik Geçmiş Bölümü */}
      <div className='max-w-5xl mx-auto'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch'>
          <div className='bg-white/80 rounded-2xl p-8 flex flex-col justify-center shadow-lg'>
            <h2 className='text-2xl font-bold text-[#181C32] mb-4'>Eğitim ve Akademik Geçmiş</h2>
            <div className='space-y-4'>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>📍 İlk ve Orta Öğretim</h3>
                <p className='text-gray-700'>
                  İlköğretimini Ankara'da tamamladı. Ankara Gazi Anadolu Lisesi'nde bir yıl hazırlık okudu, ardından ailesiyle birlikte Riyad/Suudi Arabistan'a taşındı. Orta öğretimini Riyadh American International School'da bitirdi. Türkiye'ye dönerek Ankara Atatürk Anadolu Lisesi'nden mezun oldu.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>🎓 Tıp Eğitimi</h3>
                <p className='text-gray-700'>
                  Marmara Üniversitesi Tıp Fakültesi'nden mezun oldu. Uzmanlık eğitimini İstanbul Haydarpaşa Numune Eğitim ve Araştırma Hastanesi Plastik, Rekonstrüktif ve Estetik Cerrahi Anabilim Dalı'nda tamamladı.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>🌍 Uluslararası Deneyim</h3>
                <p className='text-gray-700'>
                  Hamburg Üniversitesi (Almanya), Ağız ve Çene Cerrahisi<br />
                  Oxford Üniversitesi (İngiltere), Kraniofasiyal Cerrahi
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white/80 rounded-2xl p-8 flex flex-col justify-center shadow-lg'>
            <h2 className='text-2xl font-bold text-[#181C32] mb-4'>Akademik Yayınlar ve Mesleki Başarılar</h2>
            <div className='space-y-4'>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>📚 Akademik Başarılar</h3>
                <p className='text-gray-700'>
                  Uzmanlık tezi Aesthetic Plastic Surgery dergisinde yayınlandı:<br />
                  Cin B. Invited Response on: Effect of Rosmarinic Acid and Alcohol on Fat Graft Survival in Rat Model, 2020.<br />
                  Birçok ulusal ve uluslararası dergide makaleler yayınladı.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>🏅 Sertifikalar ve Yeterlilikler</h3>
                <p className='text-gray-700'>
                  28 Eylül 2018: Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Kurulu Sertifikası<br />
                  6 Nisan 2019: Avrupa Plastik Cerrahi Kurulu Sertifikası (Brüksel)
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>🎓 Akademik Pozisyon</h3>
                <p className='text-gray-700'>
                  2021-2022: Okan Üniversitesi'nde Öğretim Görevlisi.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-blue-700'>🏥 Tıbbi Deneyim</h3>
                <p className='text-gray-700'>
                  2021'den beri İstanbul'daki özel kliniğinde serbest hekim olarak çalışmaktadır. Bugüne kadar yüzlerce hastayı muayene etmiş ve ameliyat etmiştir.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Bölümü */}
      <section className="max-w-5xl mx-auto px-4 mt-8 pb-16">
        <div className="space-y-4">
          {/* Accordion Item: Eğitim */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group" open>
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">Eğitim</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Marmara Üniversitesi Tıp Fakültesi (İstanbul, Türkiye) 2003-2009</li>
              <li>Çankaya Atatürk Anadolu Lisesi (Ankara, Türkiye) 2000-2003</li>
              <li>American International School (Riyad, Suudi Arabistan) 1998-2000</li>
              <li>Gazi Anadolu Ortaokulu İngilizce Hazırlık 1997-1998 (Ankara, Türkiye)</li>
              <li>Eryaman Bahar İlkokulu (Ankara, Türkiye) 1992-1997</li>
            </ul>
          </details>
          
          {/* Accordion Item: Kurul Sertifikaları */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group">
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">Kurul Sertifikaları</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Kurulu, 28 Eylül 2018</li>
              <li>Avrupa Plastik, Rekonstrüktif ve Estetik Cerrahi Kurulu, 6 Nisan 2019</li>
            </ul>
          </details>
          
          {/* Accordion Item: İş Deneyimi */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group">
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">İş Deneyimi</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Özel Muayenehane (Barış Çin Kliniği – Estinn) 2021-Günümüz</li>
              <li>Okan Üniversitesi 2021-2022</li>
              <li>Sultan Abdülhamit Han Eğitim ve Araştırma Hastanesi, Ekim 2020 – Aralık 2020<br/>Plastik, Rekonstrüktif ve Estetik Cerrahi<br/>Uzman Hekim – Bölüm Şefi</li>
              <li>Haydarpaşa Eğitim ve Araştırma Hastanesi, Nisan 2020 – Ekim 2021<br/>Plastik, Rekonstrüktif ve Estetik Cerrahi<br/>Uzman Hekim – Konsültan</li>
              <li>Sultan 1. Murat Devlet Hastanesi, Şubat 2020 – Mart 2020<br/>Plastik, Rekonstrüktif ve Estetik Cerrahi<br/>Yanık Ünitesi ve El Replantasyon Merkezi (Edirne, Türkiye)</li>
              <li>Sultan 1. Murat Devlet Hastanesi, Kasım 2018 – Nisan 2020<br/>Plastik, Rekonstrüktif ve Estetik Cerrahi<br/>Yanık Ünitesi ve El Replantasyon Merkezi<br/>Uzman Hekim (Edirne, Türkiye)</li>
              <li>Haydarpaşa Eğitim ve Araştırma Hastanesi, Nisan 2016 – Kasım 2018<br/>Plastik, Rekonstrüktif ve Estetik Cerrahi<br/>Asistanlık (İstanbul, Türkiye)</li>
              <li>Oxford Kraniofasiyal Ünitesi, John Radcliffe Hastanesi, Eylül 2017<br/>Oxford Üniversitesi<br/>Gözlemcilik (Oxford, İngiltere)</li>
              <li>Ağız ve Çene Cerrahisi Kliniği, Ekim 2016<br/>Universitatsklinikum Eppendorf<br/>Gözlemcilik (Hamburg, Almanya)</li>
              <li>Bağcılar Eğitim ve Araştırma Hastanesi, Ağustos 2013 – Nisan 2016<br/>Plastik, Rekonstrüktif ve Estetik Cerrahi<br/>Asistanlık (İstanbul, Türkiye)</li>
              <li>Ataşehir Toplum Sağlığı Merkezi (İstanbul, Türkiye) Eylül 2012 – Ağustos 2013</li>
              <li>Zorunlu Askerlik Hizmeti (Şanlıurfa 1. Sınır Piyade) Ağustos 2011 – Temmuz 2012</li>
              <li>Hacettepe Üniversitesi Genel Cerrahi Asistanlığı, Ağustos 2011 – Aralık 2011 (Ankara, Türkiye)</li>
              <li>Mimar Sinan Sağlık Ocağı'nda Pratisyen Hekim, Eylül 2009 – Aralık 2009 ve 112 Ambulans Hizmetleri İstasyonu Ambulans Doktoru (Gebze-Kocaeli, Türkiye)</li>
            </ul>
          </details>
          
          {/* Accordion Item: Dergi Yayınları */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group">
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">Dergi Yayınları</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Cin Baris. Invited Response on: Effect of Rosmarinic Acid and Alcohol on Fat Graft Survival in Rat Model [published online ahead of print, 2020 Mar 23]. Aesthetic Plast Surg. 2020;10.1007/s00266-020-01687-1. doi:10.1007/s00266-020-01687-1</li>
              <li>Cin, Baris., Ciloglu, Nesibe Sinem, Omar, S., & Terzi, N. K. (2020). Effect of rosmarinic acid and alcohol on fat graft survival in rat model. Aesthetic plastic surgery, 44(1), 177-185.</li>
              <li>İskenderoğlu, Ayşe İskender, Çiloğlu, N. S., & Çin, B. Kubital Tünel Sendromu Hakkındaki Deneyimlerimiz. Haydarpaşa Numune Eğitim ve Araştırma Hastanesi Tıp Dergisi, 59(4), 0-0.</li>
            </ul>
          </details>
          
          {/* Accordion Item: Sözlü Sunumlar */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group">
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">Sözlü Sunumlar</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Nesibe Sinem Ciloglu, Baris Cin, Erinç Evrensel, Serkan Kaya, "Son 2 Yıldır SPY Cihazının Günlük Klinik Pratiğimize Katkısı", Uluslararası İş Kazaları, El Yaralanmaları ve Amputasyonlar Kongresi, İstanbul, 2019</li>
              <li>Nesibe Sinem Ciloglu, Semih Yildiz, Serkan Kaya, Y.E. Yilmazer, Barış Çin, "Subkutan Mastektomi ve Anında Meme Rekonstrüksiyonu Hastalarında İndosiyanin Yeşili Anjiyografisi ile Flep Canlılığının Değerlendirilmesi", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>Nesibe Sinem Ciloglu, Serkan Kaya, Barış Çin, Erinç Evrensel, Ozan Aslan, "Malign Melanom Hastaları ve Sentinel Lenf Nodu Biyopsileri ile Deneyimlerimiz", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>Nesibe Sinem Ciloglu, Erinç Evrensel, Semih Yildiz, Ayşe İskender. İskenderoglu, Barış Çin, "2013 – 2017 Yılları Arasında Ameliyat Edilen Bazal Hücreli Karsinom Hastalarının Gözden Geçirilmesi", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>Shahrukh Omar, Nesibe Sinem Ciloglu, A. K. Yigit, Bora Edim Akalın, Barış Çin, "Rosmarinik Asidin Mcfarlane Flep Modelinde Nekroz Önleyici Etkisi", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>K. Yigit, Nesibe Sinem Çiloğlu, Barış Çin, Ayşe İskender İskenderoglu, "Kas İskemi Reperfüzyon Sıçan Modelinde Rosmarinik Asidin Nekroz Önleyici Etkisi", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>Nesibe Sinem Çiloğlu, Barış Çin, Ahmet Kürşat Yiğit, Shahrukh Omar, "Anında Meme Rekonstrüksiyonunda Dermal Flep Kullanımı", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Nesibe Sinem Çiloğlu, Barış Çin, Ahmet Kürşat Yiğit, Shahrukh Omar "Parmak Ucu Amputasyonlarında Rekonstrüktif Deneyimlerimiz", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Nesibe Sinem Çiloğlu, Shahrukh Omar, Ayşe İskender İskenderoğlu, Bora Edim Akalın, Ahmet Kürşat Yiğit, Barış Çin, "Postbariatrik Abdominoplastide Fleur-de Lys İnsizyon'un Estetik Sonuçlara Etkisi", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Nesibe Sinem Çiloğlu, Ozan Aslan, Ahmet Kürşat Yiğit, Shahrukh Omar, Barış Çin, "Kol Germe İşlemlerine Yaklaşımımız", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Nesibe Sinem Çiloğlu, Ahmet Kürşat Yiğit, Ayşe İskender İskenderoğlu, Bora Edim Akalın, Barış Çin, Shahrukh Omar, "Saçlı Deri Bölgesi Kitlelerinde Klinik Deneyimlerimiz", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Asuman Özel, Oğuzhan Demirel, Salih Onur Basat, İbrahim Karaca Başaran, Funda Aköz Saylan, Ö. Pilanci, Mehmet Bozkurt, A. Datli, Barış Çin (İstanbul/TR), "Hipertrofik Yanık Skarlarının Tedavi Modalitesi Olarak Otolog Yağ Lipofillinginin Avantajları ve Klinik Sonuçları", Avrupa Yanık Derneği Kongresi, Hannover, 2015.</li>
            </ul>
          </details>
          
          {/* Accordion Item: Poster Sunumları */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group">
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">Poster Sunumları</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Semih Yıldız, Erinç Evrensel, Barış Çin, Nesibe Sinem Çiloğlu, "Median Sinir Varyasyonları: Erken Dallanma Gösteren 3. Ortak Dijital Sinirlerin Kesilmesi", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>Bora Edim Akalin, Erinç Evrensel, Barış Çin, Yunus Emre Yilmazer, Sinem Eroglu, "Temporal Arter Bazlı Ada Flep ve Mukoza Grefti Kullanılarak Sol Alt Göz Kapağı Tam Kat Defektinin Rekonstrüksiyonu", Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, Antalya, 2018</li>
              <li>Sinem Eroğlu, Ayşe İskender İskenderoğlu, Shahrukh Omar, Ahmet Kürşat Yiğit, Barış Çin, "Nadir Bir Olgu: İzole 4. Metakarpal Kısalığı", Türk Plastik ve Rekonstrüktif Cerrahi 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Ayşe İskender İskenderoğlu, Ahmet Kürşat Yiğit, Shahrukh Omar, Barış Çin, Nesibe Sinem Çiloğlu, "Nadir Bir Olgu: Sternumda Yerleşimli Dev BCC", Türk Plastik ve Rekonstrüktif Cerrahi 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Ozan Aslan, Shahrukh Omar, Barış Çin, Ahmet Kürşat Yiğit, Serkan Kaya, Nesibe Sinem Çiloğlu, "Sobayı Öpen Genç Kız", Türk Plastik ve Rekonstrüktif Cerrahi 39. Ulusal Kongresi, Antalya, 2017</li>
              <li>Ayşe İskender İskenderoğlu, Ahmet Kürşat Yiğit, Shahrukh Omar, Barış Çin, Yunus Emre Yılmazer, Nesibe Sinem Çiloğlu, "Anjiyografi Sonrası Pediatrik Olguda Geniş Dermal Nekroz", Türk Plastik ve Rekonstrüktif Cerrahi 39. Ulusal Kongresi, Antalya, 2017</li>
            </ul>
          </details>
          
          {/* Accordion Item: Katıldığı Kurslar ve Kongreler */}
          <details className="bg-white/80 rounded-2xl shadow-lg p-6 group">
            <summary className="cursor-pointer text-xl font-semibold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 mb-2">Katıldığı Kurslar ve Kongreler</summary>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Uluslararası İş Kazaları Kongresi, 26-27 Nisan 2019 El Yaralanmaları ve Amputasyonlar, İstanbul</li>
              <li>Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 40. Ulusal Kongresi, 17-21 Ekim 2018, Antalya</li>
              <li>1. Kartal Yara Sempozyumu 09-10 Mart 2018</li>
              <li>Üst Ekstremite Tendon Transferleri 4. Semineri 22-24 Şubat 2018 Taze Dondurulmuş Kadavra Örnekleri Üzerinde Selanik, Yunanistan</li>
              <li>Estetik Plastik Cerrahi Derneği 22. Ulusal Kongresi, 13-14 Ocak 2018, İstanbul</li>
              <li>Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği 39. Ulusal Kongresi, 11-14 Ekim 2017, Antalya</li>
              <li>TPRECD X. Rinoplasti ve III. Yüz Estetiği 11-14 Mayıs 2017 Kadavra Diseksiyon Kursu, Edirne</li>
              <li>Türk Plastik, Rekonstrüktif, Estetik Cerrahi Derneği – X. Yıllık Asistan Okulu – Antalya, 1-5 Nisan 2017</li>
              <li>Avrupa Rinoplasti Derneği Kadavra Kursu, İstanbul 18-19 Mart 2017</li>
              <li>16. Avrupa Yanık Derneği (EBA) Kongresi 16–19 Eylül 2015 Hannover, Almanya</li>
              <li>Bağcılar Eğitim ve Araştırma Hastanesi Hayvan Kullanımı ve Deneysel Araştırma Sertifika Kursu, 20-29 Mayıs 2015</li>
              <li>Türk Plastik Rekonstrüktif ve Estetik Cerrahi Derneği 36. Yıllık Konferansı, 29 Ekim – 1 Kasım 2014</li>
              <li>5. Uluslararası Avrasya Estetik Plastik Cerrahi Kursu – ISAPS Kursu, 19-23 Haziran 2013</li>
              <li>İstanbul İleri Rinoplasti Kursu 19-23 Haziran 2013</li>
              <li>Avrupa Rinoplasti Derneği 2. Yıllık Toplantısı 19-23 Haziran 2013</li>
              <li>24. Yıllık EURAPS Toplantısı (Antalya) 23-25 Mayıs 2013</li>
              <li>Ayaktan Mekanik Ventilasyon Kursu 17 Kasım 2009 Kocaeli Devlet Hastanesi Genel Cerrahi YBÜ Anestezi ve Reanimasyon Anabilim Dalı</li>
              <li>İnterosseöz Kanülasyon Kursu 26 Ekim 2009 İzmit Seka Devlet Hastanesi</li>
              <li>Aile Planlaması Sağlık Hizmetleri Kursu 6-10 Ekim 2008</li>
              <li>Üreme Sağlığı Hizmetleri Kursu 22-26 Eylül 2008 Marmara Üniversitesi Tıp Fakültesi Halk Sağlığı Anabilim Dalı</li>
              <li>Bağışıklama Hizmetleri Kursu 15-18 Eylül 2008 Marmara Üniversitesi Tıp Fakültesi Halk Sağlığı Anabilim Dalı</li>
              <li>Erken Başlangıçlı Psikiyatrik Bozukluklar Konferansı 14-17 Kasım 2007 Adölesan Günleri</li>
              <li>İstanbul Meme Kanseri Konferansı 14-16 Eylül 2006</li>
            </ul>
          </details>
        </div>
      </section>
    </>
  );
} 